import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { Upload, Video, Loader2, Info, CheckCircle2, FileVideo, X, Hash, FolderOpen, Music2, Camera, AtSign, Building2, Send, Crop } from "lucide-react";
import HashtagPicker from "../components/HashtagPicker";
import MusicPicker from "../components/MusicPicker";
import CameraRecorder from "../components/CameraRecorder";
import VideoEditor from "../components/VideoEditor";
import UserPickerSheet from "../components/UserPickerSheet";
import { cropVideoTo916, isBrowserCropSupported } from "../lib/cropTo916";

const MAX_MB = 500; // matches Emergent Object Store backend limit

export default function UploadReel() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const draftId = search.get("draft");
  const fileRef = useRef(null);
  const [cloudEnabled, setCloudEnabled] = useState(false);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploaded, setUploaded] = useState(null); // { secure_url, public_id, duration }
  const [aspect, setAspect] = useState(null); // { w, h, ratio } — read from selected file's metadata
  const [cropping, setCropping] = useState(false); // true while auto-crop is running
  const [cropProgress, setCropProgress] = useState(0);
  const [form, setForm] = useState({ title: "", description: "", category: "trending" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(draftId || null);
  const [hashOpen, setHashOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [collabOpen, setCollabOpen] = useState(false);
  const [taggedUsers, setTaggedUsers] = useState([]); // [{id, username, ...}]
  const [collabUsers, setCollabUsers] = useState([]);
  const [collabMsg, setCollabMsg] = useState("");
  const [longVideoWarn, setLongVideoWarn] = useState(null); // { file, duration_seconds }
  const [musicOpen, setMusicOpen] = useState(false);
  const [music, setMusic] = useState(null); // {id, title, artist, url, ...}
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [edits, setEdits] = useState(null); // {filter_id, filter_css, speed, trim, overlays}
  const [editorOpen, setEditorOpen] = useState(false);

  useEffect(() => {
    api.get("/integrations/status").then(r => {
      setCloudEnabled(!!r.data?.cloudinary);
    }).catch(() => {});
  }, []);

  // Resume from draft if ?draft=<id> in URL
  useEffect(() => {
    if (!draftId) return;
    api.get("/drafts").then(r => {
      const d = (r.data || []).find(x => x.id === draftId);
      if (!d) return;
      setForm(f => ({ ...f, title: d.title || "", description: d.description || "", category: d.category || "trending" }));
      // Restore the previously-uploaded video whenever we have a URL, even if
      // cloudinary_public_id is missing (legacy drafts saved before that field
      // was persisted). This lets the user tap Publish directly.
      if (d.video_url) {
        setUploaded({ secure_url: d.video_url, public_id: d.cloudinary_public_id || d.video_url, duration: d.duration_seconds });
      }
      setCurrentDraftId(draftId);
    });
  }, [draftId]);

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    acceptFile(f);
  };

  const acceptFile = (f) => {
    if (!f.type.startsWith("video/")) { setErr("Please select a video file"); return; }
    if (f.size > MAX_MB * 1024 * 1024) { setErr(`Video must be under ${MAX_MB}MB`); return; }
    setErr("");
    const v = document.createElement("video");
    v.preload = "metadata";
    v.onloadedmetadata = () => {
      const dur = Math.round(v.duration || 0);
      // Capture dimensions BEFORE revoking the object URL so we can badge
      // aspect-ratio suitability for a 9:16 reels feed.
      const w = v.videoWidth || 0, h = v.videoHeight || 0;
      setAspect(w && h ? { w, h, ratio: w / h } : null);
      window.URL.revokeObjectURL(v.src);
      if (dur > 90) {
        setLongVideoWarn({ file: f, duration_seconds: dur });
      } else {
        setFile(f); setUploaded(null); setProgress(0);
      }
    };
    v.onerror = () => { setFile(f); setUploaded(null); setProgress(0); setAspect(null); };
    v.src = window.URL.createObjectURL(f);
  };

  const confirmLongVideo = () => {
    if (longVideoWarn?.file) {
      setFile(longVideoWarn.file); setUploaded(null); setProgress(0);
    }
    setLongVideoWarn(null);
  };

  const cancelLongVideo = () => {
    setLongVideoWarn(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  // System-trim: record the first 60 seconds of the source video via captureStream + MediaRecorder,
  // return the trimmed Blob and set it as the new upload target.
  const [trimming, setTrimming] = useState(false);
  const systemTrimTo60 = async () => {
    if (!longVideoWarn?.file) return;
    setTrimming(true); setErr("");
    try {
      const srcFile = longVideoWarn.file;
      const url = URL.createObjectURL(srcFile);
      const video = document.createElement("video");
      video.src = url; video.muted = true; video.playsInline = true;
      await new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;
        video.onerror = () => reject(new Error("Cannot read video"));
      });
      // Use captureStream + MediaRecorder to grab first 60s
      const stream = video.captureStream ? video.captureStream() : video.mozCaptureStream?.();
      if (!stream) throw new Error("Your browser doesn't support in-place trimming. Please trim in your gallery app first.");
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus"
                     : MediaRecorder.isTypeSupported("video/webm;codecs=vp8,opus") ? "video/webm;codecs=vp8,opus"
                     : "video/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      const done = new Promise(res => { recorder.onstop = () => res(); });
      video.currentTime = 0;
      await video.play();
      recorder.start();
      const stopTimer = setTimeout(() => { try { recorder.stop(); video.pause(); } catch { /* noop */ } }, 60_500);
      await done;
      clearTimeout(stopTimer);
      URL.revokeObjectURL(url);
      const blob = new Blob(chunks, { type: mimeType });
      const trimmed = new File([blob], (srcFile.name || "reel").replace(/\.[^.]+$/, "") + "-60s.webm", { type: mimeType });
      setFile(trimmed); setUploaded(null); setProgress(0);
      setLongVideoWarn(null);
    } catch (e) {
      setErr(e.message || "Trim failed. Try uploading a shorter clip.");
    } finally {
      setTrimming(false);
    }
  };

  const uploadToCloudinary = async () => {
    // Note: kept the function name for back-compat. Internally uploads to Emergent Object Store.
    if (!file) throw new Error("Pick a video first");
    const fd = new FormData();
    fd.append("file", file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api/upload/file?folder=reels";
      xhr.open("POST", apiBase);
      const tok = localStorage.getItem("hf_token");
      if (tok) xhr.setRequestHeader("Authorization", "Bearer " + tok);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            // Normalise to Cloudinary-compatible shape so the rest of the form logic keeps working
            resolve({
              secure_url: data.secure_url || data.url,
              public_id: data.path,
              duration: data.duration || 0,
              bytes: data.size,
              format: data.format,
            });
          } else reject(new Error(data?.detail || "Upload failed"));
        } catch { reject(new Error("Upload failed")); }
      };
      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.send(fd);
    });
  };

  const saveAsDraft = async (extraPayload = {}, silent = false) => {
    setErr("");
    try {
      const payload = {
        title: form.title || "",
        description: form.description || "",
        category: form.category || "general",
        video_url: extraPayload.video_url ?? (uploaded?.secure_url || ""),
        cloudinary_public_id: extraPayload.cloudinary_public_id ?? (uploaded?.public_id || null),
        duration_seconds: extraPayload.duration_seconds ?? (uploaded?.duration ? Math.round(uploaded.duration) : null),
        thumbnail_url: "",
      };
      let res;
      if (currentDraftId) {
        res = await api.patch(`/drafts/${currentDraftId}`, payload);
      } else {
        res = await api.post("/drafts", payload);
        setCurrentDraftId(res.data.id);
      }
      return res.data;
    } catch (e) {
      if (!silent) setErr("Could not save draft: " + (formatErr(e) || e.message));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    let uploadedNow = uploaded;
    try {
      let payload = { ...form };
      if (!uploaded) {
        let data;
        try {
          data = await uploadToCloudinary();
        } catch (upErr) {
          // Upload failed → preserve what user has in a draft so nothing is lost
          await saveAsDraft({}, true);
          throw upErr;
        }
        uploadedNow = data;
        setUploaded(data);
        payload = { ...form, video_url: data.secure_url, cloudinary_public_id: data.public_id, duration: data.duration };
      } else {
        payload = { ...form, video_url: uploaded.secure_url, cloudinary_public_id: uploaded.public_id, duration: uploaded.duration };
      }
      if (music) payload.music = { id: music.id, title: music.title, artist: music.artist, url: music.url };
      if (edits) payload.edits = edits;
      if (taggedUsers.length > 0) payload.tagged_user_ids = taggedUsers.map(u => u.id);
      try {
        const { data: reelData } = await api.post("/reels", payload);
        // Fire collab invites (best-effort, don't fail publish if this fails)
        if (collabUsers.length > 0) {
          try {
            await api.post("/reels/collab-invite", {
              reel_id: reelData?.id,
              user_ids: collabUsers.map(u => u.id),
              message: collabMsg || "",
            });
          } catch { /* ignore invite errors */ }
        }
      } catch (postErr) {
        // Final publish failed but media is uploaded → keep a draft so the user can retry later
        await saveAsDraft({
          video_url: uploadedNow?.secure_url || payload.video_url,
          cloudinary_public_id: uploadedNow?.public_id || null,
          duration_seconds: uploadedNow?.duration ? Math.round(uploadedNow.duration) : null,
        }, true);
        throw postErr;
      }
      // Success — if we were resuming a draft, remove it
      if (currentDraftId) {
        try { await api.delete(`/drafts/${currentDraftId}`); } catch { /* ignore */ }
      }
      nav("/");
    } catch (e) {
      setErr((formatErr(e) || e.message) + " — saved to Drafts so nothing is lost.");
    }
    finally { setLoading(false); }
  };

  const resetFile = () => { setFile(null); setUploaded(null); setProgress(0); setAspect(null); if (fileRef.current) fileRef.current.value = ""; };

  const runAutoCrop916 = async () => {
    if (!file || cropping) return;
    setErr(""); setCropping(true); setCropProgress(0);
    try {
      const cropped = await cropVideoTo916(file, { onProgress: p => setCropProgress(Math.round(p * 100)) });
      // Re-read metadata so the aspect badge flips to ✅ perfect
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () => {
        setAspect({ w: v.videoWidth || 720, h: v.videoHeight || 1280, ratio: (v.videoWidth || 720) / (v.videoHeight || 1280) });
        window.URL.revokeObjectURL(v.src);
      };
      v.src = window.URL.createObjectURL(cropped);
      setFile(cropped); setUploaded(null); setProgress(0);
    } catch (e) {
      setErr(e?.message || "Auto-crop failed on this browser.");
    } finally { setCropping(false); }
  };

  return (
    <div className="px-4 py-6">
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">{currentDraftId ? "Resume Reel" : "Create Reel"}</h1>
        <Link to="/drafts" data-testid="goto-drafts" className="px-3 py-1.5 rounded-full bg-[#FF7A00]/10 border border-[#FF7A00]/40 text-[#FF7A00] text-[11px] font-bold flex items-center gap-1">
          <FolderOpen className="w-3.5 h-3.5"/>My Drafts
        </Link>
      </div>
      <p className="text-white/60 text-sm mb-5">
        Upload directly from your gallery or record with the in-app editor • up to {MAX_MB}MB
      </p>

      <div className="mb-5 p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex gap-2 text-xs">
        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
        <p className="text-white/80">Tap the box below to pick a video from your phone gallery, or open the camera to record one. <b className="text-emerald-400">If publishing fails, the reel is auto-saved to Drafts</b> so nothing is lost.</p>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="upload-form">
        <div>
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Video file</label>
            {!file ? (
              <>
                <button type="button" onClick={() => setChooserOpen(true)} data-testid="upload-dropzone"
                  className="mt-1 flex flex-col items-center justify-center gap-2 w-full py-10 rounded-2xl border-2 border-dashed border-white/15 bg-white/5 hover:border-[#00E5FF]/60 active:scale-[0.99] transition">
                  <FileVideo className="w-8 h-8 text-white/50" />
                  <span className="text-sm text-white/80 font-bold">Tap to add video</span>
                  <span className="text-xs text-white/40">Choose: Gallery · or open Camera to record</span>
                </button>
                <input ref={fileRef} type="file" accept="video/*" onChange={pickFile} className="hidden" data-testid="upload-file-input" />
              </>
            ) : (
              <div className="mt-1 p-3 rounded-2xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2">
                  <FileVideo className="w-4 h-4 text-[#00E5FF]" />
                  <span className="text-sm flex-1 truncate">{file.name}</span>
                  <span className="text-xs text-white/50">{(file.size / (1024 * 1024)).toFixed(1)}MB</span>
                  <button type="button" onClick={resetFile} className="p-1 rounded-full hover:bg-white/10" data-testid="upload-file-clear">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Aspect-ratio badge — nudges creators toward 9:16 uploads
                    for maximum immersion. If the browser supports client-side
                    crop (MediaRecorder), also expose a one-tap "Auto-crop to
                    9:16" button that produces a webm centered on the frame. */}
                {aspect && (() => {
                  const r = aspect.ratio;
                  const perfect = r >= 0.50 && r <= 0.62;
                  const square  = r >= 0.90 && r <= 1.10;
                  const wide    = r > 1.10;
                  const cls = perfect
                    ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-300"
                    : wide
                    ? "bg-[#FF2A5F]/15 border-[#FF2A5F]/40 text-[#FF2A5F]"
                    : "bg-[#FF7A00]/15 border-[#FF7A00]/40 text-[#FF7A00]";
                  const icon = perfect ? "✅" : wide ? "⚠️" : "ℹ️";
                  const msg = perfect
                    ? `Perfect 9:16 vertical (${aspect.w}×${aspect.h})`
                    : square
                    ? `Square video (${aspect.w}×${aspect.h}) — will letterbox on the feed`
                    : wide
                    ? `Wide / landscape (${aspect.w}×${aspect.h}) — will letterbox with black bars. Prefer a 9:16 crop for the best reels look`
                    : `Tall video (${aspect.w}×${aspect.h}) — will letterbox on the feed`;
                  const canCrop = !perfect && isBrowserCropSupported();
                  return (
                    <div data-testid="upload-aspect-badge" className={`mt-2 text-[11px] px-3 py-2 rounded-xl border ${cls}`}>
                      <div className="flex items-start gap-2">
                        <span className="text-base leading-none">{icon}</span>
                        <span className="flex-1 leading-snug">{msg}</span>
                      </div>
                      {canCrop && (
                        cropping ? (
                          <div className="mt-2 flex items-center gap-2 text-white/80">
                            <Loader2 className="w-3.5 h-3.5 animate-spin"/>
                            <span className="text-[11px]">Cropping… {cropProgress}%</span>
                          </div>
                        ) : (
                          <button type="button" onClick={runAutoCrop916}
                            data-testid="upload-autocrop-btn"
                            className="mt-2 w-full py-1.5 rounded-full bg-white/12 hover:bg-white/20 border border-white/25 text-white text-[11px] font-extrabold flex items-center justify-center gap-1.5 active:scale-95 transition">
                            <Crop className="w-3 h-3"/>Auto-crop to 9:16
                          </button>
                        )
                      )}
                    </div>
                  );
                })()}

                {/* Phase 2/3 Editor button — available for BOTH gallery and camera uploads */}
                <button type="button" onClick={() => setEditorOpen(true)} data-testid="upload-edit-btn"
                  className="mt-2 w-full py-2.5 rounded-2xl bg-gradient-to-r from-[#A855F7] to-[#FF2A5F] text-white font-bold text-sm flex items-center justify-center gap-2 active:scale-95">
                  ✨ Edit · Filters · Speed · Text · Stickers
                  {edits && edits.filter_id !== "none" && <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full">{edits.filter_id}</span>}
                </button>

                {(progress > 0 || uploaded) && (
                  <div className="mt-3">
                    <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-[#00E5FF] transition-all" style={{ width: `${uploaded ? 100 : progress}%` }} />
                    </div>
                    <div className="text-xs text-white/60 mt-1">
                      {uploaded ? <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Uploaded</span> : `Uploading… ${progress}%`}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Background Music <span className="text-white/40 normal-case font-normal">(optional)</span></label>
          {music ? (
            <div className="mt-1 p-3 rounded-2xl bg-[#A855F7]/15 border border-[#A855F7]/40 flex items-center gap-2" data-testid="upload-music-selected">
              <Music2 className="w-4 h-4 text-[#A855F7]"/>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{music.title}</div>
                <div className="text-[10px] text-white/55">{music.artist} · {music.category}</div>
              </div>
              <button type="button" onClick={() => setMusicOpen(true)} className="text-[11px] text-[#A855F7] font-bold px-2 py-1 rounded-full bg-white/5">Change</button>
              <button type="button" onClick={() => setMusic(null)} className="p-1 rounded-full hover:bg-white/10"><X className="w-3.5 h-3.5"/></button>
            </div>
          ) : (
            <button type="button" onClick={() => setMusicOpen(true)} data-testid="upload-music-open"
              className="mt-1 w-full p-3 rounded-2xl bg-white/5 border border-dashed border-white/15 flex items-center gap-2 active:scale-[0.99]">
              <Music2 className="w-4 h-4 text-[#A855F7]"/>
              <span className="flex-1 text-left text-sm text-white/70">Pick a track or upload your own</span>
              <span className="text-[10px] text-[#A855F7] font-bold">Open →</span>
            </button>
          )}
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Caption / Description <span className="text-white/40 normal-case font-normal">(optional)</span></label>
          <textarea data-testid="upload-desc" rows={4} value={form.description}
            onChange={(e) => {
              const v = e.target.value;
              setForm(f => ({ ...f, description: v }));
              // Auto-open Hifireels trending hashtag picker the moment user types '#'
              if (v.endsWith("#") && !hashOpen) setHashOpen(true);
            }}
            placeholder="Write a caption or leave empty. Type # for trending hashtags — you can also add your own (e.g. #mydance #city)."
            className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 focus:border-[#00E5FF] outline-none" />
          <button type="button" onClick={() => setHashOpen(true)} data-testid="open-hashtag-picker"
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-[#00E5FF] text-xs font-bold active:scale-95">
            <Hash className="w-3.5 h-3.5"/>Pick from trending hashtags
          </button>
          <p className="text-[10px] text-white/40 mt-1.5">Optional: Hifireels trending hashtags get extra reach, but you can also write your own.</p>
        </div>

        {/* Tag Hifireels users */}
        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Tag Users</label>
            <button type="button" onClick={() => setTagOpen(true)} data-testid="open-tag-users"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-[#00E5FF] text-xs font-bold active:scale-95">
              <AtSign className="w-3.5 h-3.5"/>{taggedUsers.length > 0 ? `Tagged (${taggedUsers.length})` : "Tag users"}
            </button>
          </div>
          {taggedUsers.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="tagged-chips">
              {taggedUsers.map(u => (
                <span key={u.id} data-testid={`tagged-${u.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] text-xs font-bold">
                  @{u.username}
                  <button type="button" onClick={() => setTaggedUsers(l => l.filter(x => x.id !== u.id))} className="p-0.5 rounded-full hover:bg-white/10"><X className="w-3 h-3"/></button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Invite for collaboration (brand or real user) */}
        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Invite for Collaboration</label>
            <button type="button" onClick={() => setCollabOpen(true)} data-testid="open-collab-invite"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FF7A00]/10 border border-[#FF7A00]/40 text-[#FF7A00] text-xs font-bold active:scale-95">
              <Building2 className="w-3.5 h-3.5"/>{collabUsers.length > 0 ? `Invited (${collabUsers.length})` : "Invite brand / creator"}
            </button>
          </div>
          {collabUsers.length > 0 && (
            <div className="mt-2 space-y-2" data-testid="collab-chips">
              <div className="flex flex-wrap gap-1.5">
                {collabUsers.map(u => (
                  <span key={u.id} data-testid={`collab-${u.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FF7A00]/15 border border-[#FF7A00]/40 text-[#FF7A00] text-xs font-bold">
                    @{u.username}
                    <button type="button" onClick={() => setCollabUsers(l => l.filter(x => x.id !== u.id))} className="p-0.5 rounded-full hover:bg-white/10"><X className="w-3 h-3"/></button>
                  </span>
                ))}
              </div>
              <textarea rows={2} maxLength={280} value={collabMsg} onChange={e => setCollabMsg(e.target.value)}
                placeholder="Optional message to send with the collab invite (280 chars)…"
                data-testid="collab-message"
                className="w-full px-3 py-2 rounded-2xl bg-white/5 border border-white/10 outline-none text-sm placeholder:text-white/40"/>
            </div>
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/60 font-semibold">Category</label>
          <select data-testid="upload-cat" value={form.category} onChange={upd("category")} className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none">
            {["trending", "comedy", "music", "dance", "travel", "lifestyle", "food", "sports", "auto", "scifi", "animation"].map(c => <option key={c} value={c} className="bg-[#0B0F19]">{c}</option>)}
          </select>
        </div>
        {err && <div className="text-[#FF2A5F] text-sm" data-testid="upload-err">{err}</div>}
        <button data-testid="upload-submit" disabled={loading || !file && !uploaded} className="w-full py-3.5 rounded-full bg-[#00E5FF] text-black font-outfit font-bold neon-cyan active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {!uploaded ? "Uploading…" : "Publishing…"}</> : <><Upload className="w-4 h-4" /> Publish Reel</>}
        </button>
        <p className="text-[10px] text-white/40 text-center">If publishing fails for any reason, your reel is <b className="text-[#FF7A00]">auto-saved to Drafts</b> so you can publish it again without losing work.</p>
      </form>
      <HashtagPicker open={hashOpen} onClose={() => setHashOpen(false)}
        onPick={(tag) => setForm(f => {
          // Replace trailing lone '#' with the chosen hashtag, else append at end
          const d = f.description || "";
          const next = d.endsWith("#") ? d.slice(0, -1) + tag + " " : (d + (d && !d.endsWith(" ") ? " " : "") + tag + " ");
          return { ...f, description: next };
        })}/>

      <UserPickerSheet open={tagOpen} onClose={() => setTagOpen(false)} kind="tag"
        onDone={(list) => setTaggedUsers(list)} title="Tag Hifireels users"/>

      <UserPickerSheet open={collabOpen} onClose={() => setCollabOpen(false)} kind="collab"
        onDone={(list) => setCollabUsers(list)} title="Invite for Collaboration"/>

      <MusicPicker open={musicOpen} onClose={() => setMusicOpen(false)} onSelect={setMusic} selected={music?.id}/>

      <CameraRecorder
        open={recorderOpen}
        onClose={() => setRecorderOpen(false)}
        onDone={(recordedFile, recordedEdits) => { setRecorderOpen(false); setEdits(recordedEdits || null); acceptFile(recordedFile); }}
      />

      {/* In-place editor for gallery-uploaded videos (and re-edit of recorded ones) */}
      {editorOpen && file && (
        <VideoEditor file={file} initialEdits={edits}
          onClose={() => setEditorOpen(false)}
          onSave={(e) => { setEdits(e); setEditorOpen(false); }}
        />
      )}

      {/* Gallery / Camera chooser modal */}
      {chooserOpen && (
        <div className="fixed inset-0 z-[65] bg-black/70 backdrop-blur flex items-end sm:items-center justify-center" onClick={() => setChooserOpen(false)} data-testid="upload-chooser">
          <div onClick={e => e.stopPropagation()} className="w-full sm:max-w-sm bg-[#0B0F19] border-t sm:border border-white/15 sm:rounded-3xl rounded-t-3xl p-5">
            <h3 className="font-outfit font-bold text-xl mb-1 text-center">Add a video</h3>
            <p className="text-xs text-white/55 text-center mb-4">Pick from gallery or record a new reel using the in-app camera editor.</p>
            <div className="space-y-2.5">
              <button type="button" onClick={() => { setChooserOpen(false); fileRef.current?.click(); }}
                data-testid="chooser-gallery"
                className="w-full flex items-center gap-3 p-3.5 rounded-2xl bg-gradient-to-r from-[#00E5FF]/15 to-[#A855F7]/10 border border-[#00E5FF]/30 active:scale-[0.99]">
                <div className="w-11 h-11 rounded-full bg-[#00E5FF]/25 flex items-center justify-center"><FileVideo className="w-5 h-5 text-[#00E5FF]"/></div>
                <div className="flex-1 text-left">
                  <div className="font-bold">Upload from gallery</div>
                  <div className="text-[10px] text-white/55">Pick an existing video · max {MAX_MB}MB</div>
                </div>
              </button>
              <button type="button" onClick={() => { setChooserOpen(false); setRecorderOpen(true); }}
                data-testid="chooser-camera"
                className="w-full flex items-center gap-3 p-3.5 rounded-2xl bg-gradient-to-r from-[#FF2A5F]/15 to-[#A855F7]/10 border border-[#FF2A5F]/30 active:scale-[0.99]">
                <div className="w-11 h-11 rounded-full bg-[#FF2A5F]/25 flex items-center justify-center"><Camera className="w-5 h-5 text-[#FF2A5F]"/></div>
                <div className="flex-1 text-left">
                  <div className="font-bold">Open camera & create</div>
                  <div className="text-[10px] text-white/55">Editor · timer · length cap (full screen)</div>
                </div>
              </button>
              <button type="button" onClick={() => setChooserOpen(false)} className="w-full py-2.5 rounded-full bg-white/5 text-white/70 font-bold text-sm mt-1">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Long video advisory — duration > 2 minutes */}
      {longVideoWarn && (
        <div className="fixed inset-0 z-[95] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="long-video-warn"
          onClick={cancelLongVideo}>
          <div className="w-full max-w-sm rounded-3xl border border-[#FF7A00]/40 bg-gradient-to-br from-[#FF7A00]/10 to-[#FF2A5F]/5 p-5 backdrop-blur-xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-xl bg-[#FF7A00]/20 flex items-center justify-center text-2xl">⚠️</div>
              <div>
                <h3 className="font-outfit font-extrabold text-lg text-[#FF7A00]">Heads up — long video</h3>
                <p className="text-[11px] text-white/60">Selected duration: <b className="text-white">{Math.floor((longVideoWarn.duration_seconds || 0) / 60)}m {(longVideoWarn.duration_seconds || 0) % 60}s</b></p>
              </div>
            </div>
            <p className="text-sm text-white/85 leading-relaxed mb-3">
              Reels <b className="text-[#FF7A00]">above 90 seconds</b> may not get the reach you expect on the Hifireels feed. We recommend keeping reels <b className="text-emerald-300">60 seconds or less</b> for maximum reach.
            </p>
            <div className="rounded-xl bg-white/5 border border-white/10 p-3 mb-4">
              <p className="text-[11px] text-white/65">
                💡 <b>Quick fix:</b> Tap <b className="text-[#FF7A00]">Trim to 60s</b> below and Hifireels will automatically trim your video&apos;s first minute for you.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={systemTrimTo60} disabled={trimming} data-testid="long-video-trim"
                className="py-3 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-extrabold text-sm active:scale-95 flex items-center justify-center gap-1.5 disabled:opacity-60">
                {trimming ? <><Loader2 className="w-4 h-4 animate-spin"/>Trimming…</> : "✂️ Trim to 60s (recommended)"}
              </button>
              <div className="flex gap-2">
                <button onClick={cancelLongVideo} disabled={trimming} data-testid="long-video-cancel"
                  className="flex-1 py-3 rounded-full bg-white/10 border border-white/15 font-bold text-sm active:scale-95 disabled:opacity-60">
                  Cancel
                </button>
                <button onClick={confirmLongVideo} disabled={trimming} data-testid="long-video-continue"
                  className="flex-1 py-3 rounded-full bg-white/10 border border-white/15 font-bold text-sm active:scale-95 disabled:opacity-60">
                  Upload as-is
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
